<template>
    <div class="animated fadeIn">
        <b-card>
            <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0">
                    <i class="fa fa-fw fa-archive mr-2" />Detail Request
                </h5>
                <b-button
                    size="sm"
                    variant="outline-info"
                    @click="showRequestActivityModal"
                >
                    <i class="fa fa-history mr-1" />Riwayat Request
                </b-button>
            </div>
            <hr />
            <b-row>
                <b-col>
                    No. Request <strong>{{ data.no_request }}</strong
                    ><br />
                    Cabang
                    <strong
                        >{{ data.branch }}
                        <span v-if="data.branch_code"
                            >({{ data.branch_code }})</span
                        ></strong
                    ><br />
                    Alamat: <strong>{{ data.address }}</strong
                    ><br />
                    Jenis:
                    <strong>{{ data.is_po ? "PO" : "NON-PO" }}</strong
                    ><br />
                    Status Request:
                    <b-badge
                        :variant="getStatusBadgeRequest(data.status, data.is_po).variant"
                        :class="getStatusBadgeRequest(data.status, data.is_po).class"
                    >
                        {{ getStatusBadgeRequest(data.status, data.is_po).label }}
                    </b-badge>
                    <b-badge
                        v-if="data.canceled_at"
                        variant="danger"
                        class="ml-1"
                    >
                        SISA QTY DIBATALKAN PEI
                    </b-badge>
                </b-col>
            </b-row>

            <!-- Plain div: b-alert's fade kept the button clickable after approving. -->
            <div
                v-if="isPending && data.is_po"
                role="alert"
                class="alert alert-warning mt-3 mb-0"
            >
                <i class="fa fa-clock-o mr-1" />
                PO menunggu persetujuan PEI. Barang belum bisa dikemas sebelum
                PO disetujui.
            </div>
            <div
                v-else-if="awaitingSm"
                role="alert"
                class="alert alert-warning mt-3 mb-0 d-flex align-items-center justify-content-between"
            >
                <span>
                    <i class="fa fa-clock-o mr-1" />
                    {{
                        data.is_po
                            ? "PO sudah disetujui PEI."
                            : "Request belum disetujui."
                    }}
                    Setujui untuk mulai menyiapkan barang.
                </span>
                <b-button
                    variant="success"
                    size="sm"
                    :disabled="isApproving"
                    @click="approveRequest"
                >
                    <b-spinner v-if="isApproving" small class="mr-1" />
                    <i v-else class="fa fa-check mr-1" />Setujui Request
                </b-button>
            </div>

            <template v-if="rejectedItems.length > 0">
                <b-alert show variant="danger" class="mt-3 mb-2">
                    <i class="fa fa-exclamation-triangle mr-1" />
                    {{ rejectedItems.length }} barang ditolak PEI. Kemas ulang
                    ke box baru lewat tombol Kemas Ulang.
                </b-alert>
                <b-table
                    bordered
                    small
                    responsive
                    :items="rejectedItems"
                    :fields="rejectedFields"
                >
                    <template v-slot:cell(brand)="row">
                        {{ row.item.brand || "-" }}
                    </template>
                    <template v-slot:cell(type)="row">
                        {{ row.item.type || "-" }}
                    </template>
                    <template v-slot:cell(reason)="row">
                        {{ row.item.reason || "-" }}
                    </template>
                    <template v-slot:cell(action)="row">
                        <div class="text-nowrap">
                            <b-button
                                v-if="canPack"
                                size="sm"
                                variant="warning"
                                class="mr-1"
                                v-b-tooltip.hover.top
                                title="Kemas Ulang"
                                @click="findItem(row.item.no_faktur)"
                            >
                                <i class="fa fa-fw fa-refresh" />
                            </b-button>
                            <b-button
                                size="sm"
                                variant="primary"
                                v-b-tooltip.hover.top
                                title="Lihat Log Aktivitas"
                                @click="showLogActivityModal(row.item)"
                            >
                                <i class="fa fa-fw fa-history" />
                            </b-button>
                        </div>
                    </template>
                </b-table>
            </template>

            <h6 class="mt-4">Barang Diminta</h6>
            <b-table
                bordered
                small
                show-empty
                :busy="isBusy"
                :items="data.stocks || []"
                :fields="stockFields"
                responsive
            >
                <template v-slot:empty>
                    Tidak ada barang diminta.
                </template>
                <template v-slot:cell(brand)="row">
                    {{ row.item.brand || "-" }}
                </template>
                <template v-slot:cell(weight)="row">
                    {{ parseFloat(row.item.weight) }} gr
                </template>
                <template v-slot:cell(picked)="row">
                    <span
                        :class="{
                            'text-success font-weight-bold':
                                row.item.picked >= row.item.qty
                        }"
                        >{{ row.item.picked }}</span
                    >
                </template>
            </b-table>

            <b-card v-if="canPack" class="mt-3 mb-4" bg-variant="light">
                <h6>Kemas ke Box Baru</h6>
                <small class="text-muted d-block mb-2">
                    Input No. Faktur lalu cek barangnya sebelum ditambahkan ke
                    box. Barang yang ditolak PEI dikemas ulang lewat tombol
                    Kemas Ulang.
                </small>
                <small v-if="data.canceled_at" class="text-muted d-block mb-2">
                    Sisa qty sudah dibatalkan PEI. Hanya barang yang ditolak PEI yang bisa
                    dikemas ulang.
                </small>
                <b-row>
                    <b-col md="6">
                        <b-input-group>
                            <b-input
                                v-model="scanValue"
                                maxlength="100"
                                style="max-width: 250px"
                                placeholder="Input No. Faktur..."
                                @keydown.enter="findItem"
                            />
                            <b-input-group-append>
                                <b-button variant="dark" @click="findItem"
                                    >Cari Barang</b-button
                                >
                            </b-input-group-append>
                        </b-input-group>
                    </b-col>
                    <b-col
                        md="6"
                        class="d-flex align-items-center justify-content-end"
                    >
                        <strong class="mr-3"
                            >{{ scanned.length }} barang akan dikemas</strong
                        >
                        <b-button
                            size="sm"
                            variant="outline-danger"
                            class="mr-2"
                            :disabled="scanned.length === 0"
                            @click="confirmResetScanned"
                        >
                            <i class="fa fa-undo mr-1" /> Reset
                        </b-button>
                        <b-button
                            variant="success"
                            :disabled="scanned.length === 0"
                            @click="confirmAction"
                        >
                            <i class="fa fa-plus" /> Buat Pengiriman
                        </b-button>
                    </b-col>
                </b-row>
                <div v-if="scanned.length > 0" class="mt-3">
                    <h6>Barang yang akan dikemas</h6>
                    <b-table
                        bordered
                        small
                        responsive
                        :items="scanned"
                        :fields="scannedFields"
                    >
                        <template v-slot:cell(action)="row">
                            <b-button
                                size="sm"
                                variant="outline-danger"
                                @click="removeScanned(row.item.no_faktur)"
                            >
                                <i class="fa fa-times" />
                            </b-button>
                        </template>
                    </b-table>
                </div>
            </b-card>

            <h6 class="mt-4">Daftar Box</h6>
            <small class="text-muted d-block mb-2"
                >Cetak QR Box & Faktur lalu tempel di box sebelum
                dikirim ke PEI.</small
            >
            <b-table
                bordered
                show-empty
                responsive
                :busy="isBusy"
                :items="boxes"
                :fields="boxFields"
            >
                <template v-slot:empty>
                    Belum ada box.
                </template>
                <template v-slot:cell(no_box)="row">
                    <span class="text-nowrap">{{ row.item.no_box }}</span>
                </template>
                <template v-slot:cell(status)="row">
                    <b-badge :variant="getBoxStatusBadge(row.item.status)[0]">
                        {{ getBoxStatusBadge(row.item.status)[1] }}
                    </b-badge>
                </template>
                <template v-slot:cell(courier)="row">
                    {{ courierLabel(row.item.courier) }}
                </template>
                <template v-slot:cell(no_resi)="row">
                    {{ row.item.no_resi || "-" }}
                </template>
                <template v-slot:cell(sent_at)="row">
                    {{ $helper.formatDate(row.item.sent_at) || "-" }}
                </template>
                <template v-slot:cell(action)="row">
                    <div class="text-nowrap">
                        <b-button
                            size="sm"
                            variant="primary"
                            class="mr-1"
                            v-b-tooltip.hover.top
                            title="Detail Box"
                            @click="openBox(row.item)"
                        >
                            <i class="fa fa-fw fa-eye" />
                        </b-button>
                        <b-button
                            v-if="row.item.status === BoxStatus.CREATED"
                            size="sm"
                            variant="danger"
                            v-b-tooltip.hover.top
                            title="Hapus Box"
                            @click="deleteBox(row.item)"
                        >
                            <i class="fa fa-fw fa-trash" />
                        </b-button>
                    </div>
                </template>
            </b-table>
        </b-card>

        <b-modal
            id="m-box-detail"
            size="xl"
            :title="'Detail Box ' + (selectedBox ? selectedBox.no_box : '')"
            hide-footer
        >
            <box-detail
                v-if="selectedBox"
                :key="boxKey"
                :shipment-id="selectedBox.id"
                @changed="onBoxChanged"
                @log="showLogActivityModal({ id: $event })"
            />
        </b-modal>

        <b-modal
            id="m-confirm-action"
            title="Konfirmasi Pengiriman"
            size="lg"
            centered
            no-close-on-backdrop
            hide-footer
        >
            <span>
                Apakah Anda yakin ingin membuat pengiriman untuk
                {{ scanned.length }} barang berikut?
            </span>
            <b-table
                class="mt-2 mb-2"
                bordered
                small
                responsive
                :items="scanned"
                :fields="confirmFields"
            >
            </b-table>
            <div class="text-right mt-3">
                <b-button
                    variant="success"
                    @click="executeAction()"
                    :disabled="isSubmitting"
                >
                    <b-spinner v-if="isSubmitting" small class="mr-1" />
                    <i v-else class="fa fa-plus" /> Buat
                </b-button>
            </div>
        </b-modal>

        <b-modal
            id="m-item-to-pack"
            title="Penambahan Barang"
            centered
            no-close-on-backdrop
            hide-footer
        >
            <div v-if="check.status === 'loading'">
                Mengambil data barang, mohon menunggu...
            </div>
            <b-alert v-else-if="check.status === 'error'" show variant="danger">
                {{ check.error }}
            </b-alert>
            <div v-else-if="check.status === 'ok'">
                <h3 class="mb-0">{{ check.data.type }}</h3>
                <span>{{ check.data.kind }}</span> -
                <span>{{ check.data.brand }}</span>
                <b-badge v-if="check.data.is_repack" variant="warning" class="ml-2">
                    Kemas ulang (ditolak PEI)
                </b-badge>
                <b-row class="mt-2">
                    <b-col md="3">
                        <strong>No. Faktur</strong><br />
                        <span>{{ check.data.no_faktur }}</span>
                    </b-col>
                    <b-col md="3">
                        <strong>Berat</strong><br />
                        <span>{{ check.data.weight }}gr</span>
                    </b-col>
                    <b-col md="3">
                        <strong>Tahun</strong><br />
                        <span>{{ check.data.year || "-" }}</span>
                    </b-col>
                    <b-col md="3">
                        <strong>Sumber</strong><br />
                        <span>{{ check.data.source || "-" }}</span>
                    </b-col>
                </b-row>
            </div>
            <div class="text-right mt-3">
                <b-button
                    variant="secondary"
                    class="mr-2"
                    @click="$bvModal.hide('m-item-to-pack')"
                >
                    Batal
                </b-button>
                <b-button
                    variant="success"
                    :disabled="check.status !== 'ok'"
                    @click="addToList"
                >
                    Tambahkan ke Box
                </b-button>
            </div>
        </b-modal>

        <b-modal
            id="m-log-activity"
            title="Informasi Log Aktivitas"
            centered
            no-close-on-backdrop
            hide-footer
        >
            <div class="mb-2">
                <div
                    v-if="logs && logs.items && logs.items.length > 0"
                    class="mt-2 overflow-auto"
                    style="max-height: 300px;"
                >
                    <div
                        v-for="log in logs.items"
                        :key="log.id"
                        class="mb-2 p-2 border rounded bg-light"
                    >
                        <div
                            class="d-flex justify-content-between align-items-center mb-1"
                        >
                            <strong>{{ log.title }}</strong>
                            <small class="text-muted">{{
                                $helper.formatDate(log.created_at)
                            }}</small>
                        </div>
                        <div class="small text-muted mb-2">
                            {{ log.description }}
                        </div>
                        <div class="small">
                            <strong>Oleh:</strong> {{ log.actor || "-" }}
                            <div v-if="log.reason" class="d-block mt-1">
                                <strong>Catatan:</strong> {{ log.reason }}
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else class="mt-2 text-muted">
                    Tidak ada log aktivitas
                </div>
            </div>
        </b-modal>

        <b-modal
            id="m-request-activity"
            title="Riwayat Aktivitas Request"
            centered
            no-close-on-backdrop
            hide-footer
        >
            <div class="mb-2">
                <div
                    v-if="
                        requestLogs &&
                            requestLogs.items &&
                            requestLogs.items.length > 0
                    "
                    class="mt-2 overflow-auto"
                    style="max-height: 300px;"
                >
                    <div
                        v-for="log in requestLogs.items"
                        :key="log.id"
                        class="mb-2 p-2 border rounded bg-light"
                    >
                        <div
                            class="d-flex justify-content-between align-items-center mb-1"
                        >
                            <strong>{{ actionLabel(log.action) }}</strong>
                            <small class="text-muted">{{
                                $helper.formatDate(log.created_at)
                            }}</small>
                        </div>
                        <div class="small text-muted mb-2">
                            {{ log.description }}
                        </div>
                        <div class="small">
                            <strong>Oleh:</strong>
                            {{ log.performed_by || "-" }}
                        </div>
                    </div>
                </div>
                <div v-else class="mt-2 text-muted">
                    Tidak ada riwayat aktivitas
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import BoxDetail from "./BoxDetailStockPei.vue";
import {
    courierLabel,
    REQUEST_STATUS as RequestStatus,
    requestStatusBadge,
    BOX_STATUS,
    boxStatusBadge
} from "./pei";

export default {
    components: { BoxDetail },

    data() {
        return {
            BoxStatus: BOX_STATUS,

            selectedBox: null,
            boxKey: 0,

            isSubmitting: false,
            isApproving: false,
            isBusy: true,

            data: {},
            logs: {},
            requestLogs: {},

            scanValue: "",
            scanned: [],
            check: { status: "loading", data: {}, error: "" },

            scannedFields: [
                { key: "no_faktur", label: "No. Faktur" },
                { key: "kind", label: "Jenis" },
                { key: "brand", label: "Merk" },
                { key: "type", label: "Tipe" },
                { key: "year", label: "Tahun" },
                { key: "source", label: "Source", formatter: v => v || "-" },
                { key: "action", label: "Aksi" }
            ],

            stockFields: [
                { key: "brand", label: "Merk" },
                { key: "weight", label: "Berat" },
                { key: "year", label: "Tahun" },
                { key: "qty", label: "Diminta", thClass: "text-center", tdClass: "text-center" },
                { key: "picked", label: "Dikemas", thClass: "text-center", tdClass: "text-center" }
            ],

            rejectedFields: [
                { key: "no_faktur", label: "No. Faktur" },
                { key: "kind", label: "Jenis Item" },
                { key: "brand", label: "Merk Item" },
                { key: "type", label: "Jenis Emas" },
                { key: "no_box", label: "Dari Box" },
                { key: "reason", label: "Catatan PEI" },
                { key: "action", label: "Aksi" }
            ],

            boxFields: [
                { key: "no_box", label: "No. Box" },
                { key: "status", label: "Status" },
                { key: "courier", label: "Kurir" },
                { key: "no_resi", label: "No. Resi", tdClass: "text-nowrap" },
                {
                    key: "item_count",
                    label: "Jumlah Barang",
                    thClass: "text-center",
                    tdClass: "text-center"
                },
                { key: "sent_at", label: "Tgl. Kirim" },
                { key: "action", label: "Aksi" }
            ]
        };
    },

    methods: {
        courierLabel,

        openBox(box) {
            this.selectedBox = box;
            // Remount so the box reloads.
            this.boxKey++;
            this.$bvModal.show("m-box-detail");
        },

        approveRequest() {
            if (!confirm(`Setujui request ${this.data.no_request}? Barang bisa mulai disiapkan dan dikemas.`)) {
                return;
            }

            this.isApproving = true;
            this.$axios
                .post(`/request-pei/${this.$route.params.id}/approve`)
                .then(() => {
                    this.$helper.toastSuccess(this, "Request disetujui");
                    this.getDetail();
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => {
                    this.isApproving = false;
                });
        },

        deleteBox(box) {
            if (!confirm(`Hapus box ${box.no_box}? Semua barang akan dikeluarkan dari box.`)) {
                return;
            }

            this.$axios
                .delete(`/pei-shipment/${box.id}`)
                .then(() => {
                    this.$helper.toastSuccess(this, "Box berhasil dihapus");
                    this.getDetail();
                })
                .catch(error => this.$helper.parseError(this, error));
        },

        onBoxChanged({ deleted }) {
            if (deleted) this.$bvModal.hide("m-box-detail");
            this.getDetail();
        },

        getBoxStatusBadge: boxStatusBadge,

        storageKey() {
            return `requestStockPei_scanned_${this.$route.params.id}`;
        },

        saveScanned() {
            localStorage.setItem(this.storageKey(), JSON.stringify(this.scanned));
        },

        getDetail() {
            this.isBusy = true;

            return this.$axios
                .get("/request-pei/" + this.$route.params.id)
                .then(res => {
                    this.data = res.data.data;
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => {
                    this.isBusy = false;
                });
        },

        // Opens the check modal. Called from the button/Enter (event arg) or with an NF string (Kemas Ulang).
        findItem(value) {
            const noFaktur = (typeof value === "string" ? value : this.scanValue || "").trim();

            if (!noFaktur) {
                return this.$helper.toastError(this, "Mohon mengisi nomor faktur!");
            }

            this.check = { status: "loading", data: {}, error: "" };
            this.$bvModal.show("m-item-to-pack");

            this.$axios
                .get(`/request-pei/${this.$route.params.id}/check-item`, {
                    params: { no_faktur: noFaktur }
                })
                .then(res => {
                    this.check = { status: "ok", data: res.data.data, error: "" };
                })
                .catch(error => {
                    const msg = error.response && error.response.data && error.response.data.error;
                    this.check = {
                        status: "error",
                        data: {},
                        error: typeof msg === "string" ? msg : "Gagal mengambil data barang."
                    };
                });
        },

        // Normalized year key: null / empty -> "-" (matches backend).
        yearKey(year) {
            return year == null || year === "" ? "-" : String(year);
        },

        addToList() {
            const item = this.check.data;

            if (this.scanned.some(i => i.no_faktur === item.no_faktur)) {
                this.$helper.toastError(this, `${item.no_faktur} sudah ada di daftar`);
                return;
            }

            // Qty per line is checked by the backend on "Buat Pengiriman", across the whole list.
            this.scanned.push({
                no_faktur: item.no_faktur,
                kind: item.kind,
                brand: item.brand,
                type: item.type,
                type_id: item.type_id,
                year: this.yearKey(item.year),
                source: item.source,
                is_repack: !!item.is_repack
            });
            this.saveScanned();
            this.scanValue = "";
            this.$bvModal.hide("m-item-to-pack");
        },

        removeScanned(noFaktur) {
            this.scanned = this.scanned.filter(i => i.no_faktur !== noFaktur);
            this.saveScanned();
        },

        confirmResetScanned() {
            if (!confirm("Hapus semua barang yang sudah discan?")) return;
            this.scanned = [];
            localStorage.removeItem(this.storageKey());
        },

        confirmAction() {
            if (this.scanned.length === 0) {
                this.$helper.toastError(this, "Silakan scan barang terlebih dahulu");
                return;
            }

            this.$bvModal.show("m-confirm-action");
        },

        executeAction() {
            const total = this.scanned.length;
            this.isSubmitting = true;

            this.$axios
                .post(`/request-pei/${this.$route.params.id}/pack`, {
                    no_faktur: this.scanned.map(i => i.no_faktur)
                })
                .then(() => {
                    this.$helper.toastSuccess(this, `${total} barang berhasil dibuat pengiriman`);
                    this.scanned = [];
                    localStorage.removeItem(this.storageKey());
                    this.$bvModal.hide("m-confirm-action");
                    this.getDetail();
                })
                // On 422 the scanned list is kept so ops can remove the NFs named in the message.
                .catch(error => {
                    this.$bvModal.hide("m-confirm-action");
                    this.$helper.parseError(this, error);
                })
                .finally(() => {
                    this.isSubmitting = false;
                });
        },

        showLogActivityModal(item) {
            this.$bvModal.show("m-log-activity");
            this.getLogActivity(item);
        },

        getLogActivity(item) {
            this.isBusy = true;
            return this.$axios
                .get("request-pei/items/" + item.id + "/logs")
                .then(res => {
                    this.logs = res.data.data;
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => (this.isBusy = false));
        },

        actionLabel(action) {
            const map = {
                CREATED: "Request dibuat",
                UPDATED_PO_ITEMS: "Item PO diperbarui",
                APPROVED: "Disetujui",
                PEI_APPROVED: "Disetujui PEI",
                COMPLETED: "Selesai",
                PROCESSED: "Request diproses",
                UNPACKED: "Barang dikeluarkan dari box",
                CANCELED: "Dibatalkan"
            };
            return map[action] || action;
        },

        showRequestActivityModal() {
            this.$bvModal.show("m-request-activity");
            this.getRequestActivity();
        },

        getRequestActivity() {
            this.isBusy = true;
            return this.$axios
                .get("request-pei/" + this.$route.params.id + "/logs")
                .then(res => {
                    this.requestLogs = res.data.data;
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => (this.isBusy = false));
        },

        getStatusBadgeRequest: requestStatusBadge
    },

    computed: {
        isPending() {
            return this.data.status === RequestStatus.PENDING;
        },

        // Non-PO PENDING or PO DISETUJUI PEI.
        awaitingSm() {
            return this.data.is_po
                ? this.data.status === RequestStatus.PEI_APPROVED
                : this.isPending;
        },

        boxes() {
            return this.data.boxes || [];
        },

        rejectedItems() {
            return this.data.rejected_items || [];
        },

        // Packable once APPROVED, and still while PROCESSED (partial / multi-box).
        // After PEI cancels the rest, the backend still allows re-packing rejected NFs and refuses fresh picks.
        canPack() {
            return [RequestStatus.APPROVED, RequestStatus.PROCESSED].includes(this.data.status);
        },

        confirmFields() {
            return this.scannedFields.filter(f => f.key !== "action");
        }
    },

    created() {
        // Old format stored plain NF strings; keep only item objects.
        let saved = [];
        try {
            saved = JSON.parse(localStorage.getItem(this.storageKey()) || "[]");
            if (!Array.isArray(saved)) throw new Error("not an array");
        } catch (e) {
            saved = [];
            localStorage.removeItem(this.storageKey());
        }
        this.scanned = saved.filter(i => i && typeof i === "object" && i.no_faktur);
        // Drop the old per-item selection left by the NF-based flow.
        localStorage.removeItem(`requestStockPei_selected_${this.$route.params.id}`);
        this.getDetail();
    }
};
</script>
